/**
 * Refused organization merges as sentences: starting a merge from an
 * organization, and applying one on Merge review. The body's code picks
 * the sentence; a 404 with any other code reads that a listing is gone;
 * anything else falls to the house save sentence by status. The body's
 * reason is developer text and is never read.
 */

import { ApiError } from './api';
import { contactSentence, errorCodeOf, pageWriteText, type SupportContact } from './errorCopy';

/** One sentence per refusal code the merge routes answer with.
 *  `{contact_admin}` is the admin contact. */
export const MERGE_REFUSALS: Readonly<Record<string, string>> = {
  conflict_stale_draft:
    "One of the records was edited since you started this draft. Take another look at the choices below and apply again when you're ready.",
  conflict_same_record: "A listing can't be merged into itself. Pick a different listing as the one that stays.",
  conflict_loser_already_merged:
    'One of these listings has already been merged into another. Open the listing that stayed and start from there.',
  conflict_active_draft:
    "One of these listings is already in another merge. Finish or cancel that merge if it's yours, or ask {contact_admin} who started it.",
  invalid_state: 'This merge has already been applied or cancelled. Refresh to see where it stands.',
};

/** A 404 whose code is not listed above. */
export const MERGE_GONE = "One of these listings isn't available any more. Refresh and pick again.";

/** A refused merge as one sentence: by code first, then a 404 as gone,
 *  then the house save sentence by status. */
export function mergeRefusalText(e: unknown, contact: SupportContact): string {
  const code = errorCodeOf(e);
  const sentence = code === null ? undefined : MERGE_REFUSALS[code];
  if (sentence !== undefined) return contactSentence(sentence, contact);
  if (e instanceof ApiError && e.status === 404) return MERGE_GONE;
  return pageWriteText(e, contact);
}
